import React, { useState, useEffect, useRef } from 'react';
import { X, Printer, Download, Loader2, AlertCircle } from 'lucide-react';
import api from '../../../services/api';
import cotizacionTemplateHtml from './cotizacionTemplate.js';

const FacturaPrintModal = ({ cotizacionId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const [cotizacionData, setCotizacionData] = useState(null);
  // El template HTML se importa directamente desde el archivo JS
  const htmlContent = cotizacionTemplateHtml;

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        // api.get ya inyecta el header Authorization con el token, según el interceptor en api.js
        const response = await api.get(`/cotizaciones/${cotizacionId}`);
        if (response.data && isMounted) {
          // Si response.data es el objeto directamente, o si viene dentro de data.data
          const apiData = response.data.data || response.data;
          console.log('=== DATOS RECIBIDOS DEL BACKEND ===');
          console.log('API Data completa:', JSON.stringify(apiData, null, 2));
          console.log('Artículos:', apiData.articulos);
          
          // Formateamos para inyectar al HTML según el contrato esperado
          const formattedData = {
            numero_cotizacion: apiData.numero_cotizacion || `COT-${apiData.id}`,
            fecha_cotizacion: apiData.fecha_cotizacion || (apiData.fecha_creacion ? new Date(apiData.fecha_creacion).toLocaleDateString('es-VE') : '-'),
            cliente_nombre: apiData.cliente_nombre || apiData.empresa_nombre || '-',
            // RIF, o cedula si el cliente es persona natural sin RIF (C1).
            cliente_rif: apiData.cliente_rif || apiData.cliente_cedula || '-',
            cliente_direccion: apiData.cliente_direccion || '-',
            es_factura: !!apiData.es_factura,
            contacto_nombre: apiData.contacto_nombre || '-',
            contacto_telefono: apiData.contacto_telefono || '-',
            contacto_email: apiData.contacto_email || '-',
            concepto: apiData.concepto || apiData.solicitud_nombre || 'Suministro', 
            items: apiData.items || (apiData.articulos || []).map(art => {
               console.log('Procesando articulo DEBUG:', {
                  nombre: art.articulo_nombre || art.nombre,
                  cantidad: art.cantidad,
                  precio_unitario: art.precio_unitario,
                  precio_venta: art.precio_venta,
                  subtotal: art.subtotal,
                  margen_ganancia: art.margen_ganancia,
                  porcentaje_utilidad: art.porcentaje_utilidad
               });
               const cantidad = art.cantidad || 1;
               const precioCosto = parseFloat(art.precio_unitario || 0);
               
               // La utilidad se aplica UNA SOLA VEZ por línea, no por cantidad
               const utilidadUnaVez = parseFloat(art.margen_ganancia) || (precioCosto * (parseFloat(art.porcentaje_utilidad) || 0) / 100);
               
               // Calcular totales
               const subtotalCostos = precioCosto * cantidad;
               const totalLinea = subtotalCostos + utilidadUnaVez;
               
               // Precio unitario mostrado = costo (sin utilidad)
               const precioUnitarioMostrado = precioCosto;
               
               console.log('  -> Nueva lógica - Costo:', precioCosto, 'Cantidad:', cantidad, 'Utilidad(1x):', utilidadUnaVez, 'TotalLinea:', totalLinea);
               
               return {
                  cantidad: cantidad,
                  descripcion: art.articulo_nombre || art.nombre || 'Artículo',
                  precio_unitario: 'US$ ' + precioUnitarioMostrado.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'),
                  total: 'US$ ' + totalLinea.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
               };
            }),
            // subtotal/envio/iva/total ya vienen calculados y formateados
            // por el backend (fn_recalcular_cotizacion, unica fuente de
            // verdad -- ver B1). Antes esto reconstruia el total en el
            // cliente con un 16% de IVA escrito a mano y sin sumar envio;
            // quedaba distinto del total real de la cotizacion.
            subtotal: apiData.subtotal || 'US$ 0,00',
            envio: apiData.envio || 'US$ 0,00',
            iva: apiData.iva || 'US$ 0,00',
            iva_porcentaje: apiData.iva_porcentaje || '16%',
            total: apiData.total || apiData.subtotal || 'US$ 0,00',
            metodo_pago: apiData.metodo_pago || 'A convenir',
            vendedor: apiData.vendedor || apiData.usuario_creador_nombre || '-',
            condiciones_pago: apiData.condiciones_pago || 'Sujeto a verificación'
          };
          console.log('Datos formateados:', formattedData);
          setCotizacionData(formattedData);
        }
      } catch (err) {
        if(isMounted) {
            setError(err.response?.data?.message || err.message || 'Error al obtener la cotización');
        }
      } finally {
        if(isMounted) {
            setLoading(false);
        }
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [cotizacionId]);

  const handleIframeLoad = React.useCallback(() => {
    console.log('handleIframeLoad llamada - iframe:', !!iframeRef.current, 'data:', !!cotizacionData);
    if (iframeRef.current && cotizacionData) {
      try {
        console.log('Llamando renderCotizacion con:', cotizacionData);
        iframeRef.current.contentWindow.renderCotizacion(cotizacionData);
      } catch (err) {
        console.error('Error al inyectar datos en el Iframe:', err);
      }
    }
  }, [cotizacionData]);

  // Si data se actualiza y el iframe ya cargó, forzar renderizado
  useEffect(() => {
    handleIframeLoad();
  }, [cotizacionData, handleIframeLoad]);

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        // Fallback or error handling
        console.warn('La función descargarPDF no está disponible en el iframe.');
        iframeRef.current.contentWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Printer className="text-gray-600" />
              Vista de Impresión / PDF
            </h2>
            <p className="text-sm text-gray-500">Documento ID: {cotizacionId}</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button 
                  onClick={handleDownload}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                >
                  <Download size={18} /> Descargar
                </button>
                <button 
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
                >
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium flex items-center gap-2"
            >
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando documento...</p>
            </div>
          )}
          
          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">Error al cargar datos</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe 
            ref={iframeRef}
            srcDoc={htmlContent}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Vista de impresión"
          />
        </div>
      </div>
    </div>
  );
};

export default FacturaPrintModal;
